import { types, flow, getEnv, getParent } from 'mobx-state-tree';
import { errorClassificationLookup, rejectionTypeToPolicyTypeMap } from '@ourbranch/lookups';
import omit from 'lodash-es/omit';

import {
  mapRequestQuoteToInputType,
  hasRejection,
  formattedError,
  mapRevisedBranchPlusQuoteDetails
} from 'core/helpers/quoter.service';
import { track } from 'core/helpers/analytics';
import { withRetry } from 'core/helpers/with-retry';
import { appConfig, brandConstants } from 'brand-config';

// When false (Forreston/Oakwood/CFM), the named-insured-level licensed-state gate is
// skipped — the licensed-state check is enforced per farm location instead. (Issue #30876)
const enforceLicensedStateOnNI = () => appConfig?.featureFlags?.enforceLicensedStateOnNamedInsured !== false;
import {
  REQUEST_QUOTE,
  REQUEST_BRANCH_PLUS_QUOTE,
  SEND_CLUSTER_LINK,
  ASYNC_GENERATE_BRANCH_PLUS_OFFERS_FROM_OFFER,
  ON_BRANCH_PLUS_OFFER_GENERATED
} from './quote.queries';
import { GET_OFFER } from '../offer-store/offer-queries';

const ErrorType = types.model({
  type: types.maybe(types.string),
  code: types.union(types.string, types.number),
  message: types.maybe(types.string),
  data: types.maybe(types.frozen()),
  level: types.maybe(types.string),
  handleAs: types.maybe(types.string)
});

let subscription = null;

export const QuoteStore = types
  .model({
    loading: types.boolean,
    errors: types.array(ErrorType),
    prefillData: types.maybeNull(types.frozen()),
    overriddenProtectedFields: types.array(types.string),
    offer: types.maybeNull(types.frozen()),
    simulateSource: types.maybeNull(types.string)
  })
  .actions((self) => ({
    generateBranchPlusOffersFromOfferAction: flow(function* generateBranchPlusOffersFromOffer({
      quoteDetails,
      webUserId,
      rep,
      policyId,
      offerId,
      policyType
    }) {
      self.errors = [];
      self.overriddenProtectedFields = [];
      try {
        const { client } = getEnv(self);
        self.loading = true;

        const parent = getParent(self);
        const customerId = policyId ? policyId.slice(0, 9) : null;
        yield parent.account.sendEventAsMember({
          eventName: 'Staff Generated Branch Plus Quote - Customer',
          properties: JSON.stringify({ webUserId, customerId, staffRep: rep.username, policyType }),
          userId: customerId || webUserId
        });

        track('Staff Generated Branch Plus Quote', { webUserId, customerId, staffRep: rep.username, policyType });

        const response = yield client.query({
          query: ASYNC_GENERATE_BRANCH_PLUS_OFFERS_FROM_OFFER,
          variables: {
            quoteDetails: mapRevisedBranchPlusQuoteDetails(quoteDetails),
            policyType,
            policyId,
            offerId,
            webUserId
          }
        });

        const branchPlusResponse = yield new Promise((resolve, reject) => {
          const observable = client.subscribe({
            query: ON_BRANCH_PLUS_OFFER_GENERATED,
            variables: { taskId: response.data.asyncGenerateBranchPlusOffersFromOffer.taskId }
          });

          subscription = observable.subscribe({
            next: ({ data }) => {
              try {
                const { onBranchPlusOfferGenerated } = data;
                if (onBranchPlusOfferGenerated.error) {
                  reject(onBranchPlusOfferGenerated.error);
                } else if (onBranchPlusOfferGenerated.data) {
                  resolve(JSON.parse(onBranchPlusOfferGenerated.data));
                }
              } catch (err) {
                reject(err);
              } finally {
                subscription.unsubscribe();
              }
            },
            error: (err) => {
              reject(err);
            }
          });
        }).catch((err) => {
          subscription.unsubscribe();
          throw err;
        });

        if (branchPlusResponse?.branchPlusOffers?.length > 0 && branchPlusResponse.offerId) {
          let lowestPrice = Number.POSITIVE_INFINITY;
          let lowestPricedCarrier = '';
          const successfulOffers = [];

          branchPlusResponse.branchPlusOffers.forEach((offer) => {
            if (offer?.status === 'success') {
              successfulOffers.push(offer);

              if (offer?.policyType === policyType && offer?.premium && offer.premium < lowestPrice) {
                lowestPrice = offer.premium;
                lowestPricedCarrier = offer.carrierName;
              }
            } else {
              track('Staff Generated Branch Plus Quote Response Error', {
                webUserId,
                staffRep: rep.username,
                carrier: offer?.carrierName,
                customerId,
                policyType
              });
            }
          });

          successfulOffers.forEach((offer) => {
            track('Staff Generated Branch Plus Quote Response Success', {
              webUserId,
              premium: offer.premium,
              staffRep: rep.username,
              carrier: offer.carrierName,
              customerId,
              policyType,
              lowestPricedCarrier,
              lowestPrice: lowestPrice === Number.POSITIVE_INFINITY ? null : lowestPrice
            });
          });

          return branchPlusResponse;
        }
      } catch (error) {
        track('Staff Generated Branch Plus Quote Response Error', {
          webUserId,
          staffRep: rep.username,
          policyType
        });
        console.error('Error in requestBranchPlusQuote:', error);
        throw error;
      } finally {
        self.loading = false;
      }
    }),
    requestQuoteAction: flow(function* requestQuote(userInput, allowedStates, fromOfferPage = false, retry = true) {
      self.errors = [];
      self.overriddenProtectedFields = [];
      if (!enforceLicensedStateOnNI() || allowedStates.includes(userInput.state)) {
        // check that the user has a license for this state
        try {
          const { client } = getEnv(self);
          self.loading = true;
          track('Staff Generated Quote', { userInput });

          const {
            data: { offer },
            errors
          } = yield withRetry(
            client.query({
              query: REQUEST_QUOTE,
              variables: {
                ...mapRequestQuoteToInputType(userInput),
                ...(self.simulateSource && { simulateSource: self.simulateSource })
              },
              fetchPolicy: 'no-cache'
            }),
            'requestQuote'
          );
          if (offer) {
            self.offer = offer;
          }
          if (!offer && retry && errors?.[0]?.errorType === 'ExecutionTimeout') {
            self.requestQuoteAction(userInput, allowedStates, fromOfferPage, false);
            return;
          }
          if (errors?.length) {
            self.errors = errors.map((error) => {
              try {
                const parsed = JSON.parse(error.message);
                return {
                  code: Number(parsed.code),
                  message: parsed.message
                };
              } catch (e) {
                return e;
              }
            });
            self.loading = false;
            return;
          }

          self.offer = offer;
          self.loading = false;

          if (hasRejection(offer)) {
            self.errors = offer.rejections.map(({ type, value, data, level }) => {
              return {
                type,
                code: value,
                data: data && JSON.parse(data),
                level
              };
            });
          }

          self.fetchAffinityData();
          track('Staff New Offer Received', {
            user: userInput.rep,
            isBranchPlus: false,
            offerId: offer?.id,
            requestedPolicyType: userInput.policyType,
            errors: self.errors.map((error) => error.code)
          });
        } catch (error) {
          console.error(`Error in requestQuote: ${error}`);
          self.loading = false;
          self.handleError(error);
          track('Staff New Offer Received', {
            user: userInput.rep,
            isBranchPlus: false,
            offerId: 'No offer generated',
            requestedPolicyType: userInput.policyType,
            errors: self.errors.map((error) => error.code)
          });
        }
      } else {
        const notLicensedError = {
          message: `You are not licensed in this state, if you believe this is a mistake, please contact ${brandConstants.SHORT_NAME} at ${brandConstants.CUSTOMER_SERVICE_PHONE_LETTERS} (${brandConstants.CUSTOMER_SERVICE_PHONE}) ${brandConstants.SUPPORT_EMAIL}`
        };
        self.loading = false;
        self.handleError(notLicensedError);
      }
      if (fromOfferPage) {
        return self.offer?.id;
      }
    }),
    requestBranchPlusQuoteAction: flow(function* requestBranchPlusQuoteAction(userInput, allowedStates) {
      self.errors = [];
      self.overriddenProtectedFields = [];
      if (!enforceLicensedStateOnNI() || allowedStates.includes(userInput.state)) {
        // check that the user has a license for this state
        try {
          const { client } = getEnv(self);
          self.loading = true;
          track('Staff Generated Branch Plus Quote', { userInput });

          const response = yield withRetry(
            client.query({
              query: REQUEST_BRANCH_PLUS_QUOTE,
              variables: mapRequestQuoteToInputType(userInput),
              fetchPolicy: 'no-cache'
            }),
            'asyncGenerateBranchPlusOffers'
          );

          const { offerId } = yield new Promise((resolve, reject) => {
            const observable = client.subscribe({
              query: ON_BRANCH_PLUS_OFFER_GENERATED,
              variables: { taskId: response.data.BranchPlusOfferResponse.taskId }
            });

            const timeoutId = setTimeout(
              () => {
                subscription?.unsubscribe();
                reject(new Error('Branch Plus Timeout'));
              },
              5 * 60 * 1000
            );

            subscription = observable.subscribe({
              next: ({ data }) => {
                try {
                  const { onBranchPlusOfferGenerated } = data;
                  if (onBranchPlusOfferGenerated.error) {
                    clearTimeout(timeoutId);
                    reject(onBranchPlusOfferGenerated.error);
                  } else if (onBranchPlusOfferGenerated.data) {
                    clearTimeout(timeoutId);
                    resolve(JSON.parse(onBranchPlusOfferGenerated.data));
                  }
                } catch (err) {
                  clearTimeout(timeoutId);
                  reject(err);
                }
              },
              error: (err) => {
                clearTimeout(timeoutId);
                reject(err);
              }
            });
          }).catch((err) => {
            subscription.unsubscribe();
            throw err;
          });

          const offerResponse = yield client.query({
            query: GET_OFFER,
            variables: { offerId },
            fetchPolicy: 'no-cache'
          });

          if (offerResponse.data && offerResponse.data.offer) {
            const { offer } = offerResponse.data;
            self.offer = offer;

            if (hasRejection(offer)) {
              self.errors = offer.rejections.map(({ type, value, data, level }) => {
                return {
                  type,
                  code: value,
                  data: data && JSON.parse(data),
                  level
                };
              });
            }
          }

          self.loading = false;
          self.fetchAffinityData();
          track('Staff New Offer Received', {
            user: userInput.rep,
            isBranchPlus: true,
            offerId: self.offer?.id,
            requestedPolicyType: userInput.policyType,
            errors: self.errors.map((error) => error.code)
          });
        } catch (error) {
          console.error('Error in requestBranchPlusQuote:', error);
          self.loading = false;
          self.handleError(error);
          track('Staff New Offer Received', {
            user: userInput.rep,
            isBranchPlus: true,
            offerId: 'No offer generated',
            requestedPolicyType: userInput.policyType,
            errors: self.errors.map((error) => error.code)
          });
        }
      } else {
        const notLicensedError = {
          message: `You are not licensed in this state, if you believe this is a mistake, please contact ${brandConstants.SHORT_NAME} at ${brandConstants.CUSTOMER_SERVICE_PHONE_LETTERS} (${brandConstants.CUSTOMER_SERVICE_PHONE}) ${brandConstants.SUPPORT_EMAIL}`
        };
        self.loading = false;
        self.handleError(notLicensedError);
      }
    }),
    sendClusterLink: flow(function* sendClusterLink(offerId, email, quote, agent) {
      const { client } = getEnv(self);
      return yield client.mutate({
        mutation: SEND_CLUSTER_LINK,
        variables: {
          offerId,
          revisedQuoteDetails: quote,
          email,
          agent
        }
      });
    }),
    fetchAffinityData: flow(function* fetchAffinityData(affinity) {
      if (affinity) {
        const { affinityLookups } = getParent(self);
        yield affinityLookups.getByAffinity(affinity);
      }
    }),
    handleError: (error) => {
      const parsed = formattedError(error);
      if (parsed) {
        self.errors = [...self.errors, parsed];
      }
    },
    setPrefillData(offer, skipRejectionCheck = false, preselectBundleOption = false) {
      self.overriddenProtectedFields = [];
      if (!skipRejectionCheck) {
        if (hasRejection(offer)) {
          self.errors = offer.rejections.map((rejection) => ({
            type: rejection.type,
            code: rejection.value,
            data: rejection.data && JSON.parse(rejection.data),
            level: rejection.level
          }));
        }
      }
      const prefillData = omit(offer.quote, '__typename');
      if (preselectBundleOption) {
        prefillData.selectedOption = 'HA';
      }
      self.prefillData = prefillData;
    },
    clearPrefillData() {
      self.prefillData = null;
      self.overriddenProtectedFields = [];
    },
    clearError(code) {
      self.errors = self.errors.filter((err) => err.code !== code);
    },
    setErrorToShowAsRejection(code) {
      self.errors = self.errors.map((err) => {
        if (err.code === code) {
          return { ...err, handleAs: 'REJECTION' };
        }
        return err;
      });
    },
    getAlerts(codesArray = [], policyType) {
      if (!policyType) {
        return self.errors.filter((error) => {
          const isAlert =
            error.code &&
            ['ALERT', 'DISMISSIBLE_REJECTION', 'BYPASS_ALERT'].includes(errorClassificationLookup[Number(error.code)]);
          const handleAsAlert = error.handleAs === 'ALERT' || error.handleAs === 'BYPASS_ALERT';
          const includeAlert = codesArray.length === 0 || codesArray.includes(Number(error.code));
          return (isAlert || handleAsAlert) && includeAlert && error.handleAs !== 'REJECTION';
        });
      }
      return self.errors.filter((error) => {
        // if this is a bypass alert or dismissible rejection and the requested policy type is not in the rejection array, dont show this error
        if (error.code) {
          const classification = errorClassificationLookup[Number(error.code)];
          if (classification === 'BYPASS_ALERT' || classification === 'DISMISSIBLE_REJECTION') {
            if (!rejectionTypeToPolicyTypeMap[error.type]?.includes(policyType)) {
              return false;
            }
          }
        }
        const isAlert =
          error.code &&
          ['ALERT', 'DISMISSIBLE_REJECTION', 'BYPASS_ALERT'].includes(errorClassificationLookup[Number(error.code)]);
        const handleAsAlert = error.handleAs === 'ALERT' || error.handleAs === 'BYPASS_ALERT';
        const includeAlert = codesArray.length === 0 || codesArray.includes(Number(error.code));
        return (isAlert || handleAsAlert) && includeAlert && error.handleAs !== 'REJECTION';
      });
    },
    getBlocks() {
      return self.errors.filter((error) => {
        const isBlock = !error.code || errorClassificationLookup[Number(error.code)] === 'BLOCK';
        const handleAsBlock = error.handleAs === 'BLOCK';

        return isBlock || handleAsBlock;
      });
    },
    getOfferDetails() {
      return { offer: self.offer, alerts: self.getAlerts(), blocks: self.getBlocks(), rejections: self.rejections };
    },
    cleanup() {
      if (subscription) {
        subscription.unsubscribe();
      }
      self.loading = false;
    },
    setSimulateSource(source) {
      self.simulateSource = source || null;
    }
  }))
  .views((self) => ({
    get rejections() {
      return self.errors.filter((err) => {
        // if error code is 10000 or greater, it's a rejection
        if (
          err.code &&
          err.code >= 10000 &&
          ((!err.handleAs && !errorClassificationLookup[err.code]) || err?.handleAs === 'REJECTION')
        ) {
          return true;
        }
        return false;
      });
    }
  }));
